import { useEffect } from "react";
import {
  createBrowserRouter,
  Link,
  Navigate,
  useParams,
  useRouteError,
  isRouteErrorResponse,
} from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertCircle, RotateCcw, Home } from "lucide-react";
import { formatTrpcErrorMessage } from "@/lib/trpc-error";
import { AppShell } from "@/components/layout/app-shell";
import { WorkshopLayout } from "@/components/layout/workshop-layout";
import { ProjectShell } from "@/components/layout/project-shell";
import { AuthGuard } from "@/components/auth-guard";
import { HomeRedirect } from "@/pages/home-redirect";
import { SourcesPage } from "@/pages/sources";
import { SourceDetailPage } from "@/pages/source-detail";
import { ManualEntryPage } from "@/pages/manual-entry";
import { LoginPage } from "@/pages/login";
import { SettingsHubPage } from "@/pages/settings-hub";
import { AccountPage } from "@/pages/account";
import { ProjectsPage } from "@/pages/projects";
import { ProjectDashboardPage } from "@/pages/project-dashboard";
import { ProjectScenariosPage } from "@/pages/project-scenarios";
import { ProjectPersonasPage } from "@/pages/project-personas";
import { ProjectFindingsPage } from "@/pages/project-findings";
import { FindingDetailPage } from "@/pages/finding-detail";
import { ProjectActivityPage } from "@/pages/project-activity";
import { ScenarioDetailPage } from "@/pages/scenario-detail";
import { ScenarioEditPage } from "@/pages/scenario-edit";
import { ScenarioPersonaRedirect } from "@/pages/scenario-persona-redirect";
import { WalkthroughWorkspacePage } from "@/pages/walkthrough-workspace";
import { RunViewerPage } from "@/pages/run-viewer";
import { ProjectDiscoverPage } from "@/pages/project-discover";
import { ProjectLiveWalkthroughPage } from "@/pages/project-live-walkthrough";
import { ProjectNewPage } from "@/pages/project-new";
import { ProjectWalkthroughsPage } from "@/pages/project-walkthroughs";
import { ProjectBuildsPage } from "@/pages/project-builds";
import { BuildDetailPage } from "@/pages/build-detail";
import { ProjectUnderstandingPage } from "@/pages/project-understanding";
import { ProjectTerminologyPage } from "@/pages/project-terminology";
import { ProjectSettingsPage } from "@/pages/project-settings";
import { PersonaDetailPage } from "@/pages/persona-detail";
import { InviteAcceptPage } from "@/pages/invite-accept";
import { CliLoginPage } from "@/pages/cli-login";
import { features } from "@/lib/features";
import { EditorOnlyPage } from "@/components/layout/editor-only-page";

function LegacyOutlineRedirect() {
  const { id, outlineId } = useParams<{ id: string; outlineId: string }>();
  return <Navigate to={`/projects/${id}/scenarios/${outlineId}`} replace />;
}

function LegacyRunRedirect() {
  const { id, runId } = useParams<{ id: string; runId: string }>();
  return <Navigate to={`/projects/${id}/walkthroughs/${runId}`} replace />;
}

function LegacyBootstrapRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/projects/${id}/context`} replace />;
}

function ShareReportRedirect() {
  const { token } = useParams<{ token: string }>();

  useEffect(() => {
    if (token) {
      window.location.replace(
        `/api/share/reports/${encodeURIComponent(token)}`,
      );
    } else {
      window.location.replace("/");
    }
  }, [token]);

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-2 py-20 text-muted-foreground text-sm">
      Loading shared report…
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20">
      <span
        className="text-2xl font-bold tabular-nums text-muted-foreground"
        aria-hidden="true"
      >
        404
      </span>
      <h1 className="text-xl font-semibold tracking-tight">Page not found</h1>
      <p className="text-muted-foreground">
        The page you&apos;re looking for doesn&apos;t exist.
      </p>
      <Link to="/" className="text-primary underline">
        Back to Home
      </Link>
    </div>
  );
}

function ProjectNotFoundPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-20">
      <span
        className="text-2xl font-bold tabular-nums text-muted-foreground"
        aria-hidden="true"
      >
        404
      </span>
      <h1 className="text-xl font-semibold tracking-tight">Page not found</h1>
      <p className="text-muted-foreground">
        This page doesn&apos;t exist in this project.
      </p>
      <Button asChild variant="outline">
        <Link to={`/projects/${id}`}>Back to project overview</Link>
      </Button>
    </div>
  );
}

export function ProjectRouteErrorFallback() {
  const error = useRouteError();
  const { id } = useParams<{ id: string }>();
  console.error("[ProjectRouteErrorFallback]", error);

  let message = "An unexpected error occurred.";
  if (isRouteErrorResponse(error)) {
    message =
      error.statusText || error.data?.message || `Error ${error.status}`;
  } else if (error instanceof Error) {
    message = formatTrpcErrorMessage(error, "An unexpected error occurred.");
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="rounded-full bg-destructive/10 p-3 text-destructive">
        <AlertCircle className="h-6 w-6" />
      </div>
      <h1 className="text-xl font-semibold tracking-tight">
        Something went wrong
      </h1>
      <p className="max-w-md text-sm text-muted-foreground">{message}</p>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
          className="gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          Retry
        </Button>
        {id && (
          <Button asChild variant="default" className="gap-2">
            <Link to={`/projects/${id}`}>Back to project overview</Link>
          </Button>
        )}
      </div>
    </div>
  );
}

export function RouteErrorFallback() {
  const error = useRouteError();
  console.error("[RouteErrorFallback]", error);

  let message = "An unexpected error occurred.";
  if (isRouteErrorResponse(error)) {
    message =
      error.statusText || error.data?.message || `Error ${error.status}`;
  } else if (error instanceof Error) {
    message = formatTrpcErrorMessage(error, "An unexpected error occurred.");
  }

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 p-8 text-center">
      <div className="rounded-full bg-destructive/10 p-3 text-destructive">
        <AlertCircle className="h-6 w-6" />
      </div>
      <h1 className="text-xl font-semibold tracking-tight">
        Something went wrong
      </h1>
      <p className="max-w-md text-sm text-muted-foreground">{message}</p>
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          onClick={() => window.location.reload()}
          className="gap-2"
        >
          <RotateCcw className="h-4 w-4" />
          Retry
        </Button>
        <Button asChild variant="default" className="gap-2">
          <Link to="/">
            <Home className="h-4 w-4" />
            Home
          </Link>
        </Button>
      </div>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    errorElement: <RouteErrorFallback />,
    children: [
      { path: "login", element: <LoginPage /> },
      { path: "cli-login", element: <CliLoginPage /> },
      { path: "share/reports/:token", element: <ShareReportRedirect /> },
      { path: "share/reports", element: <Navigate to="/" replace /> },
      {
        element: <AuthGuard />,
        errorElement: <RouteErrorFallback />,
        children: [
          /* ─── Project creation wizard (must come before :id to match first) ─── */
          { path: "projects/new", element: <ProjectNewPage /> },
          /* ─── Project-scoped routes (ProjectShell validates :id) ─── */
          {
            path: "projects/:id",
            element: <ProjectShell />,
            children: [
              /* Legacy runs route redirects to walkthrough workspace */
              { path: "runs/:runId", element: <LegacyRunRedirect /> },
              /* Workshop layout (no sidebar, full bleed) for legacy viewer fallback */
              {
                element: <WorkshopLayout />,
                children: [
                  { path: "runs/:runId/legacy", element: <RunViewerPage /> },
                ],
              },
              /* Sidebar layout */
              {
                element: <AppShell />,
                children: [
                  {
                    errorElement: <ProjectRouteErrorFallback />,
                    children: [
                      { index: true, element: <ProjectDashboardPage /> },
                      {
                        path: "bootstrap",
                        element: <LegacyBootstrapRedirect />,
                      },

                      ...(features.knowledgeSources
                        ? [
                            { path: "sources", element: <SourcesPage /> },
                            {
                              path: "sources/discover",
                              element: (
                                <EditorOnlyPage
                                  title="Discover sources"
                                  task="add sources"
                                >
                                  <ProjectDiscoverPage />
                                </EditorOnlyPage>
                              ),
                            },
                            {
                              path: "sources/:sourceId",
                              element: <SourceDetailPage />,
                            },
                          ]
                        : []),
                      { path: "scenarios", element: <ProjectScenariosPage /> },
                      {
                        path: "scenarios/:outlineId",
                        element: <ScenarioDetailPage />,
                      },
                      {
                        path: "scenarios/:outlineId/edit",
                        element: (
                          <EditorOnlyPage
                            title="Manage scenario"
                            task="change this scenario"
                          >
                            <ScenarioEditPage />
                          </EditorOnlyPage>
                        ),
                      },
                      {
                        path: "scenarios/:outlineId/as/:personaId",
                        element: <ScenarioPersonaRedirect />,
                      },
                      // Legacy /outlines paths redirect to /scenarios
                      {
                        path: "outlines",
                        element: <Navigate to="../scenarios" replace />,
                      },
                      {
                        path: "outlines/:outlineId",
                        element: <LegacyOutlineRedirect />,
                      },
                      { path: "personas", element: <ProjectPersonasPage /> },
                      {
                        path: "personas/:personaId",
                        element: <PersonaDetailPage />,
                      },
                      { path: "findings", element: <ProjectFindingsPage /> },
                      {
                        path: "findings/:findingId",
                        element: <FindingDetailPage />,
                      },
                      { path: "activity", element: <ProjectActivityPage /> },
                      {
                        path: "hotspots",
                        element: (
                          <Navigate to="../findings?tab=hotspots" replace />
                        ),
                      },
                      {
                        path: "walkthroughs",
                        element: <ProjectWalkthroughsPage />,
                      },
                      {
                        path: "walkthroughs/builds",
                        element: <ProjectBuildsPage />,
                      },
                      {
                        path: "walkthroughs/builds/:buildId",
                        element: <BuildDetailPage />,
                      },
                      {
                        path: "walkthroughs/:walkthroughId",
                        element: <WalkthroughWorkspacePage />,
                      },
                      ...(features.liveWalkthrough
                        ? [
                            {
                              path: "live-walkthrough",
                              element: (
                                <EditorOnlyPage
                                  title="Live walkthrough"
                                  task="run a walkthrough"
                                >
                                  <ProjectLiveWalkthroughPage />
                                </EditorOnlyPage>
                              ),
                            },
                          ]
                        : []),
                      {
                        path: "context",
                        element: <ProjectUnderstandingPage />,
                      },
                      {
                        path: "understanding",
                        element: <Navigate to="../context" replace />,
                      },
                      ...(features.terminology
                        ? [
                            {
                              path: "terminology",
                              element: (
                                <EditorOnlyPage
                                  title="Terminology"
                                  task="curate the project terminology"
                                >
                                  <ProjectTerminologyPage />
                                </EditorOnlyPage>
                              ),
                            },
                          ]
                        : []),
                      { path: "settings", element: <ProjectSettingsPage /> },
                      {
                        path: "members",
                        element: <Navigate to="../settings?tab=team" replace />,
                      },
                      {
                        path: "exports",
                        element: <Navigate to="../settings" replace />,
                      },
                      {
                        path: "*",
                        element: <ProjectNotFoundPage />,
                      },
                    ],
                  },
                ],
              },
            ],
          },
          /* ─── Invite accept (standalone, no sidebar) ─── */
          { path: "invite/:token", element: <InviteAcceptPage /> },
          /* ─── Global routes (no project context) ─── */
          {
            element: <AppShell />,
            children: [
              {
                errorElement: <RouteErrorFallback />,
                children: [
                  { index: true, element: <HomeRedirect /> },
                  ...(features.knowledgeSources
                    ? [
                        { path: "sources", element: <SourcesPage /> },
                        { path: "sources/:id", element: <SourceDetailPage /> },
                        {
                          path: "upload",
                          element: <Navigate to="/sources" replace />,
                        },
                        {
                          path: "web-source",
                          element: <Navigate to="/sources" replace />,
                        },
                        {
                          path: "search",
                          element: <Navigate to="/sources" replace />,
                        },
                        { path: "manual-entry", element: <ManualEntryPage /> },
                      ]
                    : []),
                  {
                    path: "integration",
                    element: (
                      <Navigate to="/settings?tab=integrations" replace />
                    ),
                  },
                  { path: "settings", element: <SettingsHubPage /> },
                  { path: "account", element: <AccountPage /> },
                  { path: "projects", element: <ProjectsPage /> },
                ],
              },
            ],
          },
          /* ─── Catch-all 404 (separate from global AppShell to avoid route ambiguity) ─── */
          {
            path: "*",
            element: <AppShell />,
            children: [{ path: "*", element: <NotFoundPage /> }],
          },
        ],
      },
    ],
  },
]);
